import type {
  Degree_LinearGradient2Colors as DegreeLinearGradient2Colors,
  Degree_RgbaColor as DegreeRGBAColor,
} from '__generated__/graphql-types-do-not-use';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import { useLocation, useParams } from 'js/lib/useRouter';
import user from 'js/lib/user';

import type { Color, DegreesV1 } from 'bundles/degree-home/components/__generated__/DegreesV1';
import type { Partner } from 'bundles/degree-home/types/Degree';
import type { FullDegreeFragment } from 'bundles/degree-home/utils/__queries__/__generated__/DegreeHomeDegreeBySlug';
import useDegree from 'bundles/degree-home/utils/useDegree';
import type { MembershipStatus } from 'bundles/degree-home/utils/useDegreeMembershipStatus';
import useDegreeMembershipStatus from 'bundles/degree-home/utils/useDegreeMembershipStatus';

export type DegreeDataAndPermissions = {
  location: InjectedRouter['location'];
  userId: string;
  degreeSlug: string;
  /** @deprecated Prefer direct use of GQL type returned from useDegree in this bundle */
  degreeMetadata: DegreesV1 | undefined;
  membershipStatus: MembershipStatus;
  partners: Partner[];
  degreeLoading: boolean;
};

const colorTransformer = (input: DegreeLinearGradient2Colors | DegreeRGBAColor): Color => {
  if (input.__typename === 'Degree_RGBAColor') {
    return {
      typeName: 'rgbaColor',
      definition: input,
    };
  }

  if (input.__typename === 'Degree_LinearGradient2Colors') {
    return {
      typeName: 'linearGradient2',
      definition: {
        color1: input.color1,
        color2: input.color2,
      },
    };
  }

  throw new Error('Unknown input type for colorTransformer');
};

/** Exported only for testing */
export const _transformGqlDataToLegacyFormat = (input: FullDegreeFragment): DegreesV1 => {
  return {
    description: {
      typeName: 'cml',
      definition: {
        dtdId: input.description.dtdId,
        value: input.description.cmlValue,
      },
    },
    headerBackgroundColor: colorTransformer(input.headerBackgroundColor),
    headerImage: input.headerImageUrl,
    id: input.id,
    internalLaunchType: input.internalLaunchType,
    locale: input.locale,
    name: input.name,
    partnerIds: input.partners.map(({ id }) => parseInt(id, 10)),
    productVariant: input.productVariant,
    shortName: input.slug,
    slug: input.slug,
    supportEmailAddress: input.supportEmailAddress,
    supportLink: input.supportLink,

    // Below  items are not currently used in the FE, so they are not fetched from GQL
    courseIds: [],
    degreeCategoryIds: [],
    logo: undefined,
    s12nIds: [],
    supportLinkLabel: null,
  };
};

const useDegreeDataAndPermission = (): DegreeDataAndPermissions => {
  const { degreeSlug } = useParams();
  const location = useLocation();
  const userId = user.get()?.id?.toString() ?? '';

  const { data: degreeData, loading: degreeLoading, error: degreeError } = useDegree();

  if (degreeError) {
    throw new Error(degreeError.message);
  }

  const degreeFromGql = degreeData?.Degree?.queryBySlug;
  const degreeMetadata = degreeFromGql ? _transformGqlDataToLegacyFormat(degreeFromGql) : undefined;
  const degreeId = degreeMetadata?.id;

  // TODO: CPM-2646: refactor this out so we access it directly instead of through useDegreeDataAndPermission
  const membershipStatus = useDegreeMembershipStatus(degreeId);

  // @ts-expect-error URL: string
  const partners: Partner[] = (degreeFromGql?.partners ?? []).map((partner) => ({
    id: parseInt(partner.id, 10),
    name: partner.name,
    rectangularLogo: partner.rectangularLogo,
  }));

  return {
    location,
    userId,
    // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    degreeSlug,
    degreeMetadata,
    membershipStatus,
    partners,
    degreeLoading,
  };
};

export default useDegreeDataAndPermission;
